import React, { useState, useEffect, useCallback, useRef } from 'react';
import { InventoryCarousel } from 'reaxl-organisms';
import { clickTypes, sendClick, sendImpressions } from 'reaxl-analytics';
import axios from 'axios';
import { useDevice } from '@atc/react-device';
import { Button, Container, Flex, Heading, Box } from '@iris/react-components';
import { atcBaseUrl } from '@/constants/config';

const buttonLink = `${atcBaseUrl}/cars-for-sale/by-owner`;
const savedListingIds: string[] = [];

interface RawCar {
  vdp_heading: string;
  vdp_url: string;
  cs_listing_id: string;
  price: number;
  mileage: number | { specifications: { mileage: { value: number } } };
  model: string | { name: string };
  make: string | { name: string };
  vin: string;
  year: number;
  trim: string | { name: string };
  lsc_body_styles?: Array<{ name: string }>;
  lsc_owner_id?: string;
  lsc_listing_type?: string;
  images?: Array<{ url: string }>;
  style_id?: string;
}

interface ImageSource {
  alt: string;
  src: string;
}

interface Listing {
  title: string;
  ownerId?: string;
  id: string;
  year: number;
  mileage: { specifications: { mileage: { value: number } } };
  model: { name: string };
  make: { name: string };
  trim: { name: string };
  vin: string;
  style: string;
  images: { primary: number; sources: ImageSource[] };
  website: { href: string };
  pricingDetail: { discounts: unknown[]; salePrice: number };
  specifications: { mileage: { value: number | { specifications: { mileage: { value: number } } } } };
  listingTypes: { code: string; name: string };
  listingType: string;
  isNewlyListed: boolean;
  styleId?: string;
  lscListingType?: string;
}

type BuyerLandingVehiclesSectionProps = {
  heading: string;
  backgroundColor?: string;
  filter: {
    body_style?: string;
    price_max?: number;
  };
  sectionClassName?: string;
};

const convertData = (car: RawCar): Listing => {
  const { vdp_heading: vdpHeading, images, vdp_url: vdpUrl, price, mileage, cs_listing_id: csListingId, model, make, vin, year, trim, lsc_body_styles: lscBodyStyles, lsc_owner_id: lscOwnerId, lsc_listing_type: lscListingType, style_id: styleId } = car;
  const newListing: Listing = {
    title: vdpHeading,
    ownerId: lscOwnerId,
    id: csListingId,
    year,
    mileage: typeof mileage === 'object' ? mileage : { specifications: { mileage: { value: mileage } } },
    model: typeof model === 'object' ? model : { name: model },
    make: typeof make === 'object' ? make : { name: make },
    trim: typeof trim === 'object' ? trim : { name: trim },
    vin,
    style: lscBodyStyles && lscBodyStyles.length ? lscBodyStyles[0].name.toLowerCase() : '',
    images: {
      primary: 0,
      sources: [],
    },
    website: {
      href: vdpUrl,
    },
    pricingDetail: {
      discounts: [],
      salePrice: price,
    },
    specifications: {
      mileage: {
        value: mileage,
      }
    },
    listingTypes: { code: lscListingType || ' ', name: lscListingType || ' ' },
    listingType: lscListingType || ' ',
    isNewlyListed: false
  };
  // setting the image
  if (images && images.length) {
    for (const image of images) {
      const imageSourceObject = {
        alt: vdpHeading,
        src: '',
      };
      imageSourceObject.src = image.url;
      newListing.images.sources.push(imageSourceObject);
    }
  }

  return newListing;
};

export default function BuyerLandingVehiclesSection({ heading, backgroundColor, filter, sectionClassName = '' }: BuyerLandingVehiclesSectionProps) {
  const [carListings, setCarListings] = useState<Listing[]>([]);
  const [listingsLoaded, setListingsLoaded] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const [isOnScreen, setIsOnScreen] = useState(false);
  const [isInitialTagFire, setIsInitialTagFire] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [initialIndex, setInitialIndex] = useState(0);
  const [initialCells, setInitialCells] = useState<unknown[]>([]);

  const device = useDevice();

  // Pixall Event C4. Shop Private Seller Cars Button
  const onShopAllPrivateSellerCarsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const pixallData = {
      hasListingType: false,
      eventSource: 'button',
      label: 'cta::shop-private-seller-cars',
      pageId: 'slp_psx_buy',
      cdl: {
        event: {
          action: 'Click',
          element: 'Shop Private Seller Cars Button',
          tagIndex: 'psx-123',
        },
      }
    };
    sendClick('sycClickEvent', event, {
      isPixallEvent: true,
      pixallData,
    });
    queueMicrotask(() => {
      globalThis.location.href = buttonLink;
    });
  };

  //Event B1 Private Seller Carousel - Vehicle Impression Click Through
  const handleInventoryListingClick = useCallback(async (event: React.MouseEvent, selectedListing: Listing) => {
    event.preventDefault();
    event.stopPropagation();
    const { id, year, make, model, vin, styleId, lscListingType } = selectedListing;
    const makeValue = make?.name || make;
    const modelValue = model?.name || model;
    const pixallData = {
      label: 'pvt_lstg_im',
      listingType: 'pvt',
      pageType: 'pvt',
      pageId: 'slp_psx_buy',
      eventSource: 'vehicle',
      cdl: {
        event: {
          action: 'Click',
          element: 'Vehicle Card',
          location: 'Private Seller Filmstrip',
          tagIndex: 'filmstrip-027',
          vehicle: {
            legacyListingId: id,
            year,
            make: makeValue,
            model: modelValue,    
            vin,      
            listingType: 'Private',
            type: lscListingType,
            chromeStyleId: styleId,
            sellerType: 'Private Seller',
          },
        },
      },
    };

    sendClick('sycClickEvent', event, {
      isPixallEvent: true,
      pixallData,
      data: {
        inventoryId: selectedListing.id,
      },
    });

    // Single navigation path for all left-clicks; anchors keep href for right-click/Ctrl+click.
    if (selectedListing.website?.href) {
      globalThis.location.href = selectedListing.website.href;
    }
  }, []);
  const handleSendImpressions = useCallback((_index: number, selectedListingsIds: string[], startingIndex: number, state: { inventory: Record<string, unknown> }) => sendImpressions({
    name: 'inventoryImpressions',
    state,
    data: {
      cmp: 'pvt_v_lstg_',
      selectedItemIds: selectedListingsIds,
      pixallData: {
        impressionType: 'pvt',
        label: 'pvt_v_lstg',
        listingType: 'pvt',
        eventSource: 'vehicle',
        pageId: 'slp_psx_buy',
        cdl: {
          event: {
            action: 'View',
            element: 'Vehicle Card',
            location: 'Private Seller Filmstrip',
            tagIndex: 'filmstrip-034',
            vehicle: {
              listingType: 'Private',
            },
          },
        },
      },
      startingIndex,
    },
  }), []);

  //Event B1 Private Seller Carousel - Vehicle Filmstrip Impressions
  const handleSettle = useCallback((index: number, _direction: string, _width: number, _selectedCells: unknown[], selectedIndices: number[]) => {
    const selectedListings = (selectedIndices && selectedIndices.length) ? carListings.slice(selectedIndices[0], selectedIndices[selectedIndices.length - 1] + 1) : [];
    const selectedListingsIds = selectedListings.map(({ id }) => id);
    const state: { inventory: Record<string, unknown> } = {
      inventory: {},
    };
    // refer to getInventoryOwnerById.js, this should dig into the last case
    for (const listing of selectedListings) {
      state.inventory[listing.id] = {
        id: listing.id,
        listingType: 'private-seller',
        owner: {
          id: listing.ownerId,
        },
      };
    }
    handleSendImpressions(index, selectedListingsIds, selectedIndices[0], state);
  }, [handleSendImpressions, carListings]);
  const handleReady = (index: number, selectedCells: unknown[]) => {
    setInitialIndex(index);
    setInitialCells(selectedCells);
    setIsReady(true);
  };
  const handleInitialView = useCallback((index: number, selectedCells: unknown[]) => {
    const selectedCellsLength = selectedCells && selectedCells?.length ? selectedCells?.length : 0;
    const selectedListings = selectedCells && selectedCells.length ? carListings.slice(index, (index + selectedCells?.length)) : [];
    const selectedListingsIds = selectedListings.map(({ id }) => id);
    const state: { inventory: Record<string, unknown> } = {
      inventory: {},
    };
    // refer to getInventoryOwnerById.js, this should dig into the last case
    for (const listing of selectedListings) {
      const mileageValue = listing.mileage?.specifications?.mileage?.value || listing.mileage;
      const makeValue = listing.make?.name || listing.make;
      const modelValue = listing.model?.name || listing.model;
      const trimValue = listing.trim?.name || listing.trim;

      state.inventory[listing.id] = {
        id: listing.id,
        mileage: mileageValue,
        year: listing.year,
        make: makeValue,
        makeCode: makeValue,
        model: modelValue,
        modelCode: modelValue,
        trim: trimValue,
        trimCode: trimValue,
        style: listing.style,
        listingType: 'private-seller',
        type: 'used',
        pricingDetail: listing.pricingDetail,
        owner: {
          id: listing.ownerId,
          privateSeller: true,
        },
      };
    }
    handleSendImpressions(index, selectedListingsIds, selectedCellsLength * index, state);
  }, [handleSendImpressions, carListings]);
  const carouselSettings = {
    cellWidth: '239.2px',
    fullWidthScrollXs: !device?.is?.xs,
    fullWidthScroll: true,
    visibilityThreshold: 1,
    initialIndex: 0,
    scrollItemsCount: 0,
    onReady: handleReady,
  };
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setIsOnScreen(entry.isIntersecting);
    });
    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    setCarListings([]);
    setListingsLoaded(false);

    axios
      .get('/api/psx-buy/recent-listings', { params: filter, signal: controller.signal })
      .then((response: { data?: { result?: { cars?: RawCar[] } } }) => {
        const cars = response.data?.result?.cars;
        if (cars && cars.length > 0) {
          setCarListings(cars.map(convertData));
          setListingsLoaded(true);
        } else {
          setCarListings([]);
          setListingsLoaded(false);
        }
      })
      .catch((err: unknown) => {
        if (axios.isCancel(err)) {
          return;
        }
        console.error('Error fetching vehicle listings:', err);
      });

    return () => {
      controller.abort();
    };
  }, [filter?.body_style, filter?.price_max]);

  useEffect(() => {
    if (isOnScreen && isReady && isInitialTagFire) {
      setIsInitialTagFire(false);
      handleInitialView(initialIndex, initialCells);
    }
  }, [isOnScreen, isReady, isInitialTagFire, handleInitialView, initialIndex, initialCells]);

  return (
    <Box ref={ref}>
      {listingsLoaded && (
        <Box
          className={`buyer-landing-vehicles-carousel${sectionClassName ? ` ${sectionClassName}` : ''}`}
          data-testid="buyer-landing-vehicles-section"
          style={{ backgroundColor: backgroundColor || 'inherit' }}
        >
          <Container maxWidth="none" className="popular-vehicles-content">
            <Box className="popular-vehicles-text">
              <Heading as="h2" size="xl" className="popular-vehicles-heading">{heading}</Heading>
            </Box>
            <InventoryCarousel
              orientation={device?.is?.xs ? 'portrait' : 'landscape'}
              data-testid="inventory-carousel"
              listings={carListings}
              clickType={clickTypes.SIMILAR_INVENTORY}
              carouselSettings={carouselSettings}
              onSettle={handleSettle}
              onInventoryListingClick={handleInventoryListingClick}
              savedListingIds={savedListingIds}
              showFooter={false}
              showCompareButton={false}
              listingClassName="margin-bottom-0"
              className="blp-inventory-carousel"
            />

            <Flex justify="center" className="popular-vehicles-button iris:w-full">
              <Button
                className="shop-all-button"
                variant="tertiary"
                size="lg"
                data-testid="shop-all-button"
                onClick={onShopAllPrivateSellerCarsClick}
              >
                Shop All Private Seller Cars
              </Button>
            </Flex>
          </Container>
        </Box>
      )}
    </Box>
  );
}
